import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Github, Linkedin, Twitter, Link as LinkIcon, Mail, Phone, 
  MapPin, Download, Briefcase, GraduationCap, Code, Globe, 
  ExternalLink, Sparkles, Send, Award, FileText, Lock
} from 'lucide-react';
import { motion } from 'framer-motion';
import { 
  resumeInfoApi, educationApi, experienceApi, projectsApi, 
  skillsApi, languagesApi, awardsApi, cvGenerationApi, portfolioSettingsApi 
} from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { onAuthStateChange } from '../lib/firebase';
import LiveCodingStats from '../components/portfolio/LiveCodingStats';
import { generatePortfolioPDF } from '../utils/portfolioPdfExport';


import GlassmorphismTheme from '../components/portfolio-templates/GlassmorphismTheme';
import TerminalTheme from '../components/portfolio-templates/TerminalTheme';
import Creative3DTheme from '../components/portfolio-templates/Creative3DTheme';
import NeonGlowTheme from '../components/portfolio-templates/NeonGlowTheme';
import RetroTheme from '../components/portfolio-templates/RetroTheme';
import NewspaperTheme from '../components/portfolio-templates/NewspaperTheme';

export default function PortfolioView() {
  const { resumeId } = useParams();
  const [searchParams] = useSearchParams();
  const isPreviewMode = searchParams.get('preview') === 'true';

  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  
  // Customization settings
  const [template, setTemplate] = useState('developer');
  const [color, setColor] = useState('blue');
  const [socials, setSocials] = useState({ github: '', leetcode: '', linkedin: '', twitter: '', custom: '' });
  const [isPublished, setIsPublished] = useState(true);
  
  // Contact form state
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactMsg, setContactMsg] = useState('');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [authInitialized, setAuthInitialized] = useState(false);

  useEffect(() => {
    if (isPreviewMode) return;

    const unsubscribe = onAuthStateChange(() => {
      setAuthInitialized(true);
    });
    return () => unsubscribe();
  }, [isPreviewMode]);

  // Helper: load profile data from localStorage (saved by PortfolioBuilder)
  const loadProfileFromLocalStorage = () => {
    const previewDataKey = `cvlogs_portfolio_preview_data_${resumeId}`;
    const savedPreviewData = localStorage.getItem(previewDataKey);
    if (savedPreviewData) {
      try {
        const parsed = JSON.parse(savedPreviewData);
        setProfileData(parsed);
        return true;
      } catch (e) {
        console.error('Error parsing saved profile data:', e);
      }
    }
    return false;
  };

  useEffect(() => {
    const handleUpdate = () => {
      if (isPreviewMode) {
        loadProfileFromLocalStorage();
        setLoading(false);
      }
    };

    if (isPreviewMode) {
      handleUpdate();
      window.addEventListener('portfolio-preview-updated', handleUpdate);
      window.addEventListener('storage', handleUpdate);
      return () => {
        window.removeEventListener('portfolio-preview-updated', handleUpdate);
        window.removeEventListener('storage', handleUpdate);
      };
    }

    if (resumeId) {
      // Immediately try loading from localStorage for instant display
      const hasLocalData = loadProfileFromLocalStorage();
      if (hasLocalData) {
        setLoading(false);
      }

      // Then try the API for fresh data (will override localStorage data if successful)
      if (authInitialized) {
        fetchFullProfile(!hasLocalData);
      }
    }
  }, [resumeId, isPreviewMode, authInitialized]);

  // Read preview params or load from local storage
  const applyPublishedSettings = (settings = {}) => {
    setTemplate(settings.template || 'developer');
    setColor(settings.themeColor || 'blue');
    setSocials({
      github: settings.githubUrl || '',
      leetcode: settings.leetcodeUrl || '',
      linkedin: settings.linkedinUrl || '',
      twitter: settings.twitterUrl || '',
      custom: settings.customLink || ''
    });
    setIsPublished(settings.isPublished ?? true);
  };

  const loadPortfolioSettings = async (resumeIdToLoad) => {
    const storageKey = `cvlogs_portfolio_${resumeIdToLoad}`;
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        applyPublishedSettings(parsed);
      } catch (e) {
        console.error(e);
      }
    }

    try {
      const remoteSettings = await portfolioSettingsApi.get(resumeIdToLoad);
      if (remoteSettings) {
        applyPublishedSettings(remoteSettings);
        localStorage.setItem(storageKey, JSON.stringify(remoteSettings));
      }
    } catch (error) {
      console.warn('Portfolio settings fallback to local cache:', error);
    }
  };

  useEffect(() => {
    if (isPreviewMode) {
      setTemplate(searchParams.get('template') || 'developer');
      setColor(searchParams.get('color') || 'blue');
      setSocials({
        github: searchParams.get('github') || '',
        leetcode: searchParams.get('leetcode') || '',
        linkedin: searchParams.get('linkedin') || '',
        twitter: searchParams.get('twitter') || '',
        custom: searchParams.get('customLink') || ''
      });
      setIsPublished(true); // Always viewable in preview mode
    } else if (resumeId) {
      loadPortfolioSettings(resumeId);
    }
  }, [resumeId, isPreviewMode, searchParams]);

  const trackAnalyticsEvent = (eventType, eventDetail = '') => {
    if (!resumeId) return;
    const key = `cvlogs_portfolio_analytics_${resumeId}`;
    const raw = localStorage.getItem(key);
    let stats = {
      totalVisits: 0,
      uniqueVisitors: 0,
      socialClicks: 0,
      cvDownloads: 0,
      recentClicks: [],
      dailyVisits: { Mon: 0, Tue: 0, Wed: 0, Thu: 0, Fri: 0, Sat: 0, Sun: 0 }
    };
    
    if (raw) {
      try {
        stats = { ...stats, ...JSON.parse(raw) };
      } catch (e) {
        console.error(e);
      }
    }

    const today = new Date();
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const currentDay = dayNames[today.getDay()];

    if (eventType === 'visit') {
      stats.totalVisits += 1;
      stats.dailyVisits[currentDay] = (stats.dailyVisits[currentDay] || 0) + 1;
      
      const sessionKey = `cvlogs_portfolio_visited_${resumeId}`;
      if (!sessionStorage.getItem(sessionKey)) {
        sessionStorage.setItem(sessionKey, 'true');
        stats.uniqueVisitors += 1;
      }
    } else if (eventType === 'click') {
      stats.socialClicks += 1;
      stats.recentClicks.unshift({
        type: eventDetail,
        time: new Date().toISOString(),
        location: isPreviewMode ? 'Local Preview' : 'Web View'
      });
      stats.recentClicks = stats.recentClicks.slice(0, 5);
    } else if (eventType === 'download') {
      stats.cvDownloads += 1;
    }

    localStorage.setItem(key, JSON.stringify(stats));
    
    // Dispatch custom events for live syncing
    window.dispatchEvent(new Event('portfolio-analytics-updated'));
    try {
      window.parent.dispatchEvent(new Event('portfolio-analytics-updated'));
    } catch (e) {}
  };

  useEffect(() => {
    if (profileData && resumeId) {
      trackAnalyticsEvent('visit');
    }
  }, [profileData, resumeId]);

  const fetchFullProfile = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);

      let generatedCV = null;
      try {
        generatedCV = await cvGenerationApi.getLatest(resumeId);
      } catch (error) {
        console.log('No generated CV found, falling back to manual data');
      }

      if (generatedCV) {
        const mappedCore = {
          name: generatedCV.personal_info?.name || generatedCV.name || '',
          email: generatedCV.personal_info?.email || generatedCV.email || '',
          phone: generatedCV.personal_info?.phone || generatedCV.phone || '',
          city: generatedCV.personal_info?.city || generatedCV.city || '',
          country: generatedCV.personal_info?.country || generatedCV.country || '',
          address: generatedCV.personal_info?.address || generatedCV.address || '',
          summary: generatedCV.professional_summary || generatedCV.summary || '',
          links: generatedCV.personal_info?.links || generatedCV.links || {}
        };

        const mappedEducation = (generatedCV.education || []).map(edu => ({
          ...edu,
          school: edu.school || edu.organization_name || '',
          organization_name: edu.organization_name || edu.school || '',
          start_date: edu.start_date || edu.date || '',
          end_date: edu.end_date || edu.date || '',
          degree: edu.degree || '',
          major: edu.major || ''
        }));

        const mappedExperience = (generatedCV.experience || []).map(exp => ({
          ...exp,
          job_title: exp.job_title || exp.position || exp.title || '',
          position: exp.position || exp.job_title || '',
          company: exp.company || exp.organization || '',
          organization: exp.organization || exp.company || '',
          start_date: exp.start_date || exp.date?.split(' - ')[0] || '',
          end_date: exp.end_date || exp.date?.split(' - ')[1] || '',
          description: Array.isArray(exp.points) ? exp.points.join('\n• ') : (exp.description || exp.enhanced_description?.join('\n• ') || '')
        }));

        const mappedProjects = (generatedCV.projects || []).map(proj => ({
          ...proj,
          title: proj.title || proj.name || '',
          description: Array.isArray(proj.description) ? proj.description.join('\n') : (proj.description || proj.enhanced_description?.join('\n') || ''),
          link: proj.link || proj.url || proj.githubUrl || ''
        }));

        let skillsArray = [];
        if (Array.isArray(generatedCV.skills)) {
          skillsArray = generatedCV.skills;
        } else if (generatedCV.skills) {
          const tech = Array.isArray(generatedCV.skills.technical_skills) ? generatedCV.skills.technical_skills : [];
          const soft = Array.isArray(generatedCV.skills.soft_skills) ? generatedCV.skills.soft_skills : [];
          skillsArray = [...tech, ...soft].map(skill => typeof skill === 'string' ? { name: skill } : skill);
        }

        const mappedLanguages = (generatedCV.languages || []).map(lang => ({
          language: lang.language || lang.name || (typeof lang === 'string' ? lang : ''),
          level: lang.level || lang.proficiency || ''
        }));

        const mappedAwards = (generatedCV.awards || []).map(aw => ({
          ...aw,
          title: aw.title || '',
          organization: aw.organization || aw.issuer || '',
          date_received: aw.date_received || aw.date || ''
        }));

        const freshData = {
          core: mappedCore,
          education: mappedEducation,
          experience: mappedExperience,
          projects: mappedProjects,
          skills: skillsArray,
          languages: mappedLanguages,
          awards: mappedAwards
        };

        setProfileData(freshData);
        localStorage.setItem(`cvlogs_portfolio_preview_data_${resumeId}`, JSON.stringify(freshData));
        return;
      }

      // Fetch core info, education, experience, skills, projects, and awards in parallel
      const [core, edu, exp, proj, sk, lang, aw] = await Promise.all([
        resumeInfoApi.get(resumeId).catch(() => null),
        educationApi.list(resumeId).catch(() => []),
        experienceApi.list(resumeId).catch(() => []),
        projectsApi.list(resumeId).catch(() => []),
        skillsApi.list(resumeId).catch(() => []),
        languagesApi.list(resumeId).catch(() => []),
        awardsApi.list(resumeId).catch(() => [])
      ]);

      if (core) {
        const freshData = {
          core,
          education: Array.isArray(edu) ? edu : [],
          experience: Array.isArray(exp) ? exp : [],
          projects: Array.isArray(proj) ? proj : [],
          skills: Array.isArray(sk) ? sk : [],
          languages: Array.isArray(lang) ? lang : [],
          awards: Array.isArray(aw) ? aw : []
        };
        setProfileData(freshData);
        // Also update localStorage so future visits load instantly
        localStorage.setItem(`cvlogs_portfolio_preview_data_${resumeId}`, JSON.stringify(freshData));
      }
    } catch (e) {
      console.error('Error fetching full profile details:', e);
      // If API fails, try localStorage as final fallback
      loadProfileFromLocalStorage();
    } finally {
      setLoading(false);
    }
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    if (!contactName.trim() || !contactEmail.trim() || !contactMsg.trim()) return;
    setFormSubmitted(true);
    setTimeout(() => {
      setContactName('');
      setContactEmail('');
      setContactMsg('');
      setFormSubmitted(false);
      alert('Message sent successfully!');
    }, 1000);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <LoadingSpinner size="lg" logoSpinner={true} text="Loading Portfolio..." />
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-900 text-white p-6">
        <Lock className="w-16 h-16 text-slate-500 mb-4 animate-pulse" />
        <h2 className="text-xl font-bold mb-2">Portfolio Not Found</h2>
        <p className="text-slate-400 text-sm max-w-sm text-center">
          The requested portfolio might be configured incorrectly or does not exist on our servers.
        </p>
      </div>
    );
  }

  // Check visibility status
  if (!isPublished && !isPreviewMode) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-950 text-white p-6 text-center">
        <div className="p-4 bg-slate-900 rounded-3xl mb-4 border border-slate-800">
          <Lock className="w-12 h-12 text-blue-500" />
        </div>
        <h2 className="text-2xl font-bold mb-2">Portfolio is Private</h2>
        <p className="text-slate-400 text-sm max-w-md">
          This personal portfolio website is currently in draft mode. The owner has not published it yet.
        </p>
      </div>
    );
  }

  const { core, education, experience, projects, skills, languages, awards } = profileData;

  // Accent Colors Mapper
  const colorMap = {
    blue: { primary: 'text-blue-500', bg: 'bg-blue-600', border: 'border-blue-500/20', hover: 'hover:bg-blue-700', ring: 'focus:ring-blue-500/20', gradient: 'from-blue-500 to-indigo-600' },
    emerald: { primary: 'text-emerald-500', bg: 'bg-emerald-600', border: 'border-emerald-500/20', hover: 'hover:bg-emerald-700', ring: 'focus:ring-emerald-500/20', gradient: 'from-emerald-500 to-teal-600' },
    purple: { primary: 'text-purple-500', bg: 'bg-purple-600', border: 'border-purple-500/20', hover: 'hover:bg-purple-700', ring: 'focus:ring-purple-500/20', gradient: 'from-purple-500 to-pink-600' },
    rose: { primary: 'text-rose-500', bg: 'bg-rose-600', border: 'border-rose-500/20', hover: 'hover:bg-rose-700', ring: 'focus:ring-rose-500/20', gradient: 'from-rose-500 to-red-600' },
    slate: { primary: 'text-slate-500', bg: 'bg-slate-700', border: 'border-slate-500/20', hover: 'hover:bg-slate-800', ring: 'focus:ring-slate-500/20', gradient: 'from-slate-600 to-slate-800' }
  };

  const activeColor = colorMap[color] || colorMap.blue;

  // Framer Motion Animation Settings
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-100px' },
    transition: { duration: 0.6, cubicBezier: [0.16, 1, 0.3, 1] }
  };

  // --- TEMPLATE RENDERING ---

  if (template === 'glassmorphism') return <GlassmorphismTheme profileData={profileData} socials={socials} activeColor={activeColor} trackSocialClick={(type) => trackAnalyticsEvent('click', type)} trackDownload={() => trackAnalyticsEvent('download')} />;
  if (template === 'terminal') return <TerminalTheme profileData={profileData} socials={socials} activeColor={activeColor} trackSocialClick={(type) => trackAnalyticsEvent('click', type)} trackDownload={() => trackAnalyticsEvent('download')} />;
  if (template === 'creative3d') return <Creative3DTheme profileData={profileData} socials={socials} activeColor={activeColor} trackSocialClick={(type) => trackAnalyticsEvent('click', type)} trackDownload={() => trackAnalyticsEvent('download')} />;
  if (template === 'neonglow') return <NeonGlowTheme profileData={profileData} socials={socials} activeColor={activeColor} trackSocialClick={(type) => trackAnalyticsEvent('click', type)} trackDownload={() => trackAnalyticsEvent('download')} />;
  if (template === 'retro') return <RetroTheme profileData={profileData} socials={socials} activeColor={activeColor} trackSocialClick={(type) => trackAnalyticsEvent('click', type)} trackDownload={() => trackAnalyticsEvent('download')} />;
  if (template === 'newspaper') return <NewspaperTheme profileData={profileData} socials={socials} activeColor={activeColor} trackSocialClick={(type) => trackAnalyticsEvent('click', type)} trackDownload={() => trackAnalyticsEvent('download')} />;

  // 1. DEVELOPER (DARK THEME)
  if (template === 'developer') {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-slate-100 font-sans selection:bg-blue-500 selection:text-white pb-16 relative overflow-hidden">
        <Helmet><title>{core.name} | Developer Portfolio</title></Helmet>

        {/* Animated Background Orbs */}
        <div className="fixed top-[-10%] left-[-10%] w-[40vw] h-[40vw] bg-blue-600/20 rounded-full blur-[120px] pointer-events-none mix-blend-screen" />
        <div className="fixed bottom-[-10%] right-[-10%] w-[40vw] h-[40vw] bg-purple-600/20 rounded-full blur-[120px] pointer-events-none mix-blend-screen" />

        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/80 backdrop-blur-md border-b border-white/5">
          <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
            <span className="text-xl font-black tracking-tighter bg-gradient-to-r from-white to-white/50 bg-clip-text text-transparent">
              {core.name.split(' ')[0]}<span className={activeColor.primary}>.dev</span>
            </span>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 mr-4">
                {socials.github && <a href={socials.github} target="_blank" rel="noreferrer" className="text-white/50 hover:text-white transition-colors"><Github className="w-5 h-5" /></a>}
                {socials.linkedin && <a href={socials.linkedin} target="_blank" rel="noreferrer" className="text-white/50 hover:text-white transition-colors"><Linkedin className="w-5 h-5" /></a>}
                {socials.twitter && <a href={socials.twitter} target="_blank" rel="noreferrer" className="text-white/50 hover:text-white transition-colors"><Twitter className="w-5 h-5" /></a>}
              </div>
              <button
                onClick={() => { generatePortfolioPDF(profileData); trackAnalyticsEvent('download'); }}
                className={`flex items-center gap-2 px-4 py-2 ${activeColor.bg} ${activeColor.hover} text-white rounded-xl text-sm font-bold transition-all shadow-[0_0_20px_rgba(59,130,246,0.3)] hover:shadow-[0_0_30px_rgba(59,130,246,0.5)]`}
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Resume</span>
              </button>
            </div>
          </div>
        </nav>

        <main className="pt-32 px-6 max-w-6xl mx-auto space-y-32 relative z-10">
          
          {/* Hero Section */}
          <section className="flex flex-col items-center text-center space-y-8">
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold bg-white/5 border border-white/10 text-white/80 backdrop-blur-sm`}
            >
              <span className="relative flex h-2 w-2">
                <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${activeColor.bg} opacity-75`}></span>
                <span className={`relative inline-flex rounded-full h-2 w-2 ${activeColor.bg}`}></span>
              </span>
              Available for Opportunities
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.5 }}
              className="text-5xl sm:text-7xl font-black tracking-tight"
            >
              Hi, I'm <span className={`bg-gradient-to-r ${activeColor.gradient} bg-clip-text text-transparent`}>{core.name}</span>
            </motion.h1>

            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="text-lg sm:text-xl text-white/60 max-w-2xl leading-relaxed"
            >
              {core.summary || 'A passionate professional dedicated to building high-quality projects and solving complex technical challenges.'}
            </motion.p>
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Left Column (Experience & Projects) */}
            <div className="lg:col-span-8 space-y-8">
              
              {/* Experience */}
              {experience.length > 0 && (
                <motion.section {...fadeInUp} className="space-y-6">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg bg-white/5 border border-white/10 ${activeColor.primary}`}>
                      <Briefcase className="w-5 h-5" />
                    </div>
                    <h2 className="text-2xl font-bold">Experience</h2>
                  </div>
                  <div className="space-y-4">
                    {experience.map((exp, idx) => (
                      <div key={idx} className="group relative bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-colors">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                          <div>
                            <h3 className="text-lg font-bold text-white group-hover:text-blue-400 transition-colors">{exp.job_title}</h3>
                            <div className="text-sm text-white/60">{exp.company}</div>
                          </div>
                          <div className="text-xs font-mono px-3 py-1 bg-white/10 rounded-full w-fit text-white/70">
                            {exp.start_date} - {exp.end_date || 'Present'}
                          </div>
                        </div>
                        <p className="text-sm text-white/70 leading-relaxed">{exp.description}</p>
                      </div>
                    ))}
                  </div>
                </motion.section>
              )}

              {/* Projects */}
              {projects.length > 0 && (
                <motion.section {...fadeInUp} className="space-y-6">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg bg-white/5 border border-white/10 ${activeColor.primary}`}>
                      <Code className="w-5 h-5" />
                    </div>
                    <h2 className="text-2xl font-bold">Featured Projects</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {projects.map((proj, idx) => (
                      <div key={idx} className="group bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all flex flex-col h-full">
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-white mb-2">{proj.title}</h3>
                          <p className="text-sm text-white/60 leading-relaxed mb-6">{proj.description}</p>
                        </div>
                        {proj.link && (
                          <a href={proj.link} target="_blank" rel="noreferrer" className={`inline-flex items-center gap-2 text-sm font-bold ${activeColor.primary} group-hover:translate-x-1 transition-transform`}>
                            View Project <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </motion.section>
              )}
            </div>

            {/* Right Column (Skills, Contact) */}
            <div className="lg:col-span-4 space-y-8">
              
              {/* Skills */}
              {skills.length > 0 && (
                <motion.section {...fadeInUp} className="bg-white/5 border border-white/10 rounded-3xl p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className={`p-2 rounded-lg bg-white/5 border border-white/10 ${activeColor.primary}`}>
                      <Award className="w-5 h-5" />
                    </div>
                    <h2 className="text-xl font-bold">Skills</h2>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {skills.map((sk, idx) => (
                      <span key={idx} className="px-3 py-1.5 bg-white/5 border border-white/10 hover:border-white/20 hover:bg-white/10 transition-colors rounded-lg text-sm font-medium text-white/80">
                        {sk.name || sk}
                      </span>
                    ))}
                  </div>
                </motion.section>
              )}

              {/* Live Coding Stats */}
              {(socials.github || socials.leetcode) && (
                <motion.section {...fadeInUp}>
                   <LiveCodingStats githubUrl={socials.github} leetcodeUrl={socials.leetcode} activeColor={activeColor} themeType="dark" />
                </motion.section>
              )}

              {/* Contact Info */}
              <motion.section {...fadeInUp} className="bg-gradient-to-br from-white/5 to-white/0 border border-white/10 rounded-3xl p-8">
                <h2 className="text-xl font-bold mb-6">Let's Connect</h2>
                <div className="space-y-4">
                  {core.email && (
                    <a href={`mailto:${core.email}`} className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/5 hover:border-white/20 transition-colors group">
                      <div className="p-2 bg-white/10 rounded-lg group-hover:scale-110 transition-transform">
                        <Mail className="w-4 h-4 text-white" />
                      </div>
                      <div className="flex flex-col overflow-hidden">
                        <span className="text-xs text-white/50 font-medium">Email</span>
                        <span className="text-sm font-semibold text-white truncate">{core.email}</span>
                      </div>
                    </a>
                  )}
                  {core.phone && (
                    <div className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/5">
                      <div className="p-2 bg-white/10 rounded-lg">
                        <Phone className="w-4 h-4 text-white" />
                      </div>
                      <div className="flex flex-col overflow-hidden">
                        <span className="text-xs text-white/50 font-medium">Phone</span>
                        <span className="text-sm font-semibold text-white">{core.phone}</span>
                      </div>
                    </div>
                  )}
                  {core.city && (
                    <div className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/5">
                      <div className="p-2 bg-white/10 rounded-lg">
                        <MapPin className="w-4 h-4 text-white" />
                      </div>
                      <div className="flex flex-col overflow-hidden">
                        <span className="text-xs text-white/50 font-medium">Location</span>
                        <span className="text-sm font-semibold text-white">{core.city}, {core.country}</span>
                      </div>
                    </div>
                  )}
                </div>
              </motion.section>
              
            </div>
          </div>
        </main>
      </div>
    );
  }

  // 2. MINIMALIST (LIGHT SERIF THEME)
  if (template === 'minimalist') {
    return (
      <div className="min-h-screen bg-[#faf9f6] text-slate-800 font-serif selection:bg-slate-800 selection:text-white pb-20">
        <Helmet><title>{core.name}</title></Helmet>

        {/* Header Name */}
        <header className="max-w-3xl mx-auto px-6 pt-16 pb-8 border-b border-slate-200 text-center sm:text-left flex flex-col sm:flex-row justify-between items-center sm:items-end gap-6">
          <div>
            <h1 className="text-4xl sm:text-5xl font-light tracking-tight text-slate-900">{core.name}</h1>
            <p className="text-slate-500 italic mt-2 text-sm font-sans">{core.summary || 'Professional Portfolio'}</p>
          </div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
            <button
              onClick={() => { generatePortfolioPDF(profileData); trackAnalyticsEvent('download'); }}
              className="inline-flex items-center gap-1.5 px-4 py-2 border border-slate-300 hover:border-slate-800 hover:bg-slate-800 hover:text-white rounded text-xs font-sans font-semibold transition-all"
            >
              <Download className="w-3.5 h-3.5" />
              Download CV
            </button>
          </motion.div>
        </header>

        {/* Main Sections */}
        <main className="max-w-3xl mx-auto px-6 py-12 space-y-12">
          {/* About Summary */}
          {core.summary && (
            <motion.section {...fadeInUp} className="space-y-4">
              <h2 className="text-xs uppercase tracking-widest text-slate-400 font-sans font-bold">Profile</h2>
              <p className="text-base leading-relaxed text-slate-700 font-light text-justify">
                {core.summary}
              </p>
            </motion.section>
          )}

          {/* Experience */}
          {experience.length > 0 && (
            <motion.section {...fadeInUp} className="space-y-6">
              <h2 className="text-xs uppercase tracking-widest text-slate-400 font-sans font-bold">Experience</h2>
              <div className="space-y-6">
                {experience.map((exp, idx) => (
                  <div key={idx} className="grid grid-cols-1 sm:grid-cols-4 gap-2 border-l border-slate-200 pl-4">
                    <span className="text-xs text-slate-400 font-sans font-semibold col-span-1">
                      {exp.start_date} - {exp.end_date || 'Present'}
                    </span>
                    <div className="col-span-3 space-y-1">
                      <h3 className="text-base font-semibold text-slate-900">{exp.job_title}</h3>
                      <div className="text-xs text-slate-500 font-sans font-medium">{exp.company}</div>
                      <p className="text-xs text-slate-600 leading-relaxed text-justify mt-1">{exp.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.section>
          )}

          {/* Featured projects */}
          {projects.length > 0 && (
            <motion.section {...fadeInUp} className="space-y-6">
              <h2 className="text-xs uppercase tracking-widest text-slate-400 font-sans font-bold">Projects</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {projects.map((proj, idx) => (
                  <div key={idx} className="space-y-2 p-4 border border-slate-200/80 rounded-xl bg-white/40">
                    <h3 className="text-base font-bold text-slate-900">{proj.title}</h3>
                    <p className="text-xs text-slate-600 leading-relaxed">{proj.description}</p>
                    {proj.link && (
                      <a href={proj.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-sans text-slate-600 hover:text-slate-900 font-semibold underline">
                        <span>Project Source</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </motion.section>
          )}

          {/* Skills list */}
          {skills.length > 0 && (
            <motion.section {...fadeInUp} className="space-y-4">
              <h2 className="text-xs uppercase tracking-widest text-slate-400 font-sans font-bold">Expertise</h2>
              <div className="flex flex-wrap gap-2">
                {skills.map((sk, idx) => (
                  <span key={idx} className="px-3 py-1 bg-white border border-slate-200 rounded-lg text-xs font-sans text-slate-600">
                    {sk.name || sk}
                  </span>
                ))}
              </div>
            </motion.section>
          )}

          {/* Live Coding Stats */}
          {(socials.github || socials.leetcode) && (
            <motion.section {...fadeInUp} className="space-y-4 pt-6">
               <LiveCodingStats githubUrl={socials.github} leetcodeUrl={socials.leetcode} activeColor={activeColor} themeType="light" />
            </motion.section>
          )}

          {/* Footer Contact Details */}
          <motion.section {...fadeInUp} className="border-t border-slate-200 pt-12 text-center space-y-4">
            <h2 className="text-xs uppercase tracking-widest text-slate-400 font-sans font-bold">Contact Details</h2>
            <div className="flex flex-wrap justify-center gap-6 text-xs text-slate-600 font-sans">
              {core.email && (
                <span className="flex items-center gap-1 justify-center">
                  <Mail className="w-3.5 h-3.5" />
                  {core.email}
                </span>
              )}
              {core.phone && (
                <span className="flex items-center gap-1 justify-center">
                  <Phone className="w-3.5 h-3.5" />
                  {core.phone}
                </span>
              )}
              {core.city && (
                <span className="flex items-center gap-1 justify-center">
                  <MapPin className="w-3.5 h-3.5" />
                  {core.city}, {core.country}
                </span>
              )}
            </div>

            {/* Social Icons */}
            <div className="flex justify-center gap-3 pt-2">
              {socials.github && <a href={socials.github} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-slate-800"><Github className="w-5 h-5" /></a>}
              {socials.linkedin && <a href={socials.linkedin} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-slate-800"><Linkedin className="w-5 h-5" /></a>}
              {socials.twitter && <a href={socials.twitter} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-slate-800"><Twitter className="w-5 h-5" /></a>}
            </div>
          </motion.section>
        </main>
      </div>
    );
  }

  // 3. CORPORATE (PROFESSIONAL MULTI-COLOR THEME)
  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 font-sans selection:bg-blue-600 selection:text-white pb-16">
      <Helmet><title>{core.name} | Executive Profile</title></Helmet>

      {/* Modern Top Navigation Bar */}
      <nav className="bg-white/80 backdrop-blur-md sticky top-0 z-50 shadow-sm border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <span className="font-black tracking-tight text-lg text-slate-900">{core.name}</span>
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-3 mr-2">
              {socials.github && <a href={socials.github} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-slate-800 transition-colors"><Github className="w-4 h-4" /></a>}
              {socials.linkedin && <a href={socials.linkedin} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-slate-800 transition-colors"><Linkedin className="w-4 h-4" /></a>}
              {socials.twitter && <a href={socials.twitter} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-slate-800 transition-colors"><Twitter className="w-4 h-4" /></a>}
            </div>
            <a
              href={`mailto:${core.email}`}
              className={`px-5 py-2 ${activeColor.bg} ${activeColor.hover} text-white font-bold rounded-full text-xs transition-colors flex items-center gap-1.5 shadow-md hover:shadow-lg`}
            >
              <Mail className="w-3.5 h-3.5" />
              <span>Hire Me</span>
            </a>
          </div>
        </div>
      </nav>

      {/* Intro Header */}
      <section className="bg-slate-900 text-white py-24 px-6 relative overflow-hidden">
        {/* Subtle grid pattern background */}
        <div className="absolute inset-0 opacity-10 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/20 rounded-full blur-[100px]" />
        
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row md:items-end justify-between gap-8 relative z-10">
          <div className="max-w-3xl space-y-6">
            <motion.div 
              initial={{ opacity: 0, x: -15 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-2 text-xs font-bold text-blue-400 uppercase tracking-widest"
            >
              <Briefcase className="w-4 h-4" />
              <span>Executive Portfolio</span>
            </motion.div>
            <motion.h1 
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl sm:text-6xl font-black tracking-tight leading-tight"
            >
              {core.name}
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-slate-300 text-base sm:text-lg leading-relaxed max-w-2xl"
            >
              {core.summary}
            </motion.p>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex-shrink-0"
          >
            <button
              onClick={() => { generatePortfolioPDF(profileData); trackAnalyticsEvent('download'); }}
              className={`inline-flex items-center gap-2 px-6 py-3 bg-white text-slate-900 hover:bg-slate-100 rounded-full text-sm font-bold transition-all shadow-xl`}
            >
              <Download className="w-4 h-4" />
              Download Resume
            </button>
          </motion.div>
        </div>
      </section>

      {/* Main Body */}
      <main className="max-w-6xl mx-auto px-6 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
        
        {/* Left Column (Main Experience & Projects) */}
        <div className="lg:col-span-8 space-y-12">
          {/* Work History */}
          {experience.length > 0 && (
            <motion.section {...fadeInUp} className="space-y-6">
              <h2 className="text-2xl font-black text-slate-900 flex items-center gap-3">
                <div className={`p-2 rounded-xl bg-blue-100 ${activeColor.primary}`}>
                  <Briefcase className="w-5 h-5" />
                </div>
                Professional Experience
              </h2>

              <div className="space-y-6">
                {experience.map((exp, idx) => (
                  <div key={idx} className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-slate-200/60 hover:shadow-md transition-shadow group relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-1 h-full bg-slate-200 group-hover:bg-blue-500 transition-colors" />
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
                      <div>
                        <h3 className="text-lg font-bold text-slate-900">{exp.job_title}</h3>
                        <div className="text-sm font-semibold text-slate-500">{exp.company}</div>
                      </div>
                      <span className="text-xs font-bold text-slate-400 bg-slate-100 px-3 py-1 rounded-full w-fit">
                        {exp.start_date} - {exp.end_date || 'Present'}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed">{exp.description}</p>
                  </div>
                ))}
              </div>
            </motion.section>
          )}

          {/* Project Grid */}
          {projects.length > 0 && (
            <motion.section {...fadeInUp} className="space-y-6 pt-6">
              <h2 className="text-2xl font-black text-slate-900 flex items-center gap-3">
                <div className={`p-2 rounded-xl bg-blue-100 ${activeColor.primary}`}>
                  <Code className="w-5 h-5" />
                </div>
                Featured Projects
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {projects.map((proj, idx) => (
                  <div key={idx} className="bg-white border border-slate-200/60 rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all flex flex-col h-full group">
                    <div className="flex-1 space-y-3">
                      <h3 className="font-bold text-slate-900 text-lg group-hover:text-blue-600 transition-colors">{proj.title}</h3>
                      <p className="text-sm text-slate-600 leading-relaxed">{proj.description}</p>
                    </div>
                    {proj.link && (
                      <a href={proj.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm font-bold text-blue-600 hover:text-blue-700 mt-6 group-hover:translate-x-1 transition-transform">
                        <span>View Project</span>
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </motion.section>
          )}

          {/* Live Coding Stats */}
          {(socials.github || socials.leetcode) && (
             <motion.section {...fadeInUp} className="pt-6">
                <LiveCodingStats githubUrl={socials.github} leetcodeUrl={socials.leetcode} activeColor={activeColor} themeType="light" />
             </motion.section>
          )}
        </div>

        {/* Right Column (Skills, Details, Socials) */}
        <div className="lg:col-span-4 space-y-8">
          
          {/* Contact details */}
          <motion.div {...fadeInUp} className="bg-white border border-slate-200/60 rounded-2xl p-6 shadow-sm space-y-6">
            <h3 className="font-black text-slate-900 text-base">Contact Information</h3>
            <div className="space-y-4 text-sm text-slate-600 font-medium">
              {core.email && (
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-slate-50 rounded-lg"><Mail className="w-4 h-4 text-slate-400" /></div>
                  <span className="truncate">{core.email}</span>
                </div>
              )}
              {core.phone && (
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-slate-50 rounded-lg"><Phone className="w-4 h-4 text-slate-400" /></div>
                  <span>{core.phone}</span>
                </div>
              )}
              {core.city && (
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-slate-50 rounded-lg"><MapPin className="w-4 h-4 text-slate-400" /></div>
                  <span>{core.city}, {core.country}</span>
                </div>
              )}
            </div>
            
            {/* Social links row */}
            <div className="flex items-center gap-2 pt-4 border-t border-slate-100">
              {socials.github && <a href={socials.github} target="_blank" rel="noreferrer" className="p-2.5 bg-slate-50 hover:bg-slate-100 rounded-xl text-slate-500 hover:text-slate-900 transition-colors"><Github className="w-4 h-4" /></a>}
              {socials.linkedin && <a href={socials.linkedin} target="_blank" rel="noreferrer" className="p-2.5 bg-slate-50 hover:bg-slate-100 rounded-xl text-slate-500 hover:text-slate-900 transition-colors"><Linkedin className="w-4 h-4" /></a>}
              {socials.twitter && <a href={socials.twitter} target="_blank" rel="noreferrer" className="p-2.5 bg-slate-50 hover:bg-slate-100 rounded-xl text-slate-500 hover:text-slate-900 transition-colors"><Twitter className="w-4 h-4" /></a>}
            </div>
          </motion.div>

          {/* Skills */}
          {skills.length > 0 && (
            <motion.div {...fadeInUp} className="bg-white border border-slate-200/60 rounded-2xl p-6 shadow-sm space-y-4">
              <h3 className="font-black text-slate-900 text-base">Core Competencies</h3>
              <div className="flex flex-wrap gap-2">
                {skills.map((sk, idx) => (
                  <span key={idx} className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-600">
                    {sk.name || sk}
                  </span>
                ))}
              </div>
            </motion.div>
          )}

          {/* Education list */}
          {education.length > 0 && (
            <motion.div {...fadeInUp} className="bg-white border border-slate-200/60 rounded-2xl p-6 shadow-sm space-y-4">
              <h3 className="font-black text-slate-900 text-base">Academic Record</h3>
              <div className="space-y-5">
                {education.map((edu, idx) => (
                  <div key={idx} className="space-y-1 text-sm relative pl-4 border-l-2 border-slate-100">
                    <div className="font-bold text-slate-900 leading-snug">{edu.degree}</div>
                    <div className="text-slate-500 font-semibold">{edu.school}</div>
                    <div className="text-slate-400 text-xs font-bold">{edu.start_date} - {edu.end_date || 'Present'}</div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

        </div>

      </main>

    </div>
  );
}
